// TanStack Query keys, options and hooks. Keys are hierarchical so SSE events can invalidate
// exactly the lists/threads they touch.
import { useCallback, useSyncExternalStore } from "react";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import type {
  AccountSummary,
  SessionInfo,
  ThreadListResponse,
  ThreadSummary,
  ThreadView,
} from "@shared/api";
import { browserStorage, clearAllRecovery } from "@web/compose/recovery";
import { setCsrfToken } from "./api";
import { endpoints, type LoginBody, type SetupBody, type ThreadListView } from "./endpoints";
import { nextCursor } from "./listing";

export interface ThreadListKey {
  view: ThreadListView;
  accountId?: string | undefined;
  /** A Gmail label id from the rail; takes precedence over `view`. */
  label?: string | undefined;
}

export const queryKeys = {
  session: ["session"] as const,
  accounts: ["accounts"] as const,
  threads: (params: ThreadListKey) =>
    [
      "threads",
      {
        view: params.view,
        accountId: params.accountId,
        ...(params.label ? { label: params.label } : {}),
      },
    ] as const,
  threadAll: (key: string) => ["thread", key] as const,
  thread: (key: string, images: boolean) => ["thread", key, { images }] as const,
  search: (q: string) => ["search", q.trim()] as const,
};

// ---------------------------------------------------------------------------------------------
// Session

export const sessionQuery = queryOptions({
  queryKey: queryKeys.session,
  queryFn: async ({ signal }) => {
    const info = await endpoints.session(signal);
    setCsrfToken(info.csrfToken);
    return info;
  },
  staleTime: 5 * 60_000,
  retry: 2,
});

export function useSession() {
  return useQuery(sessionQuery);
}

export function acceptSession(queryClient: QueryClient, info: SessionInfo): void {
  setCsrfToken(info.csrfToken);
  queryClient.setQueryData(queryKeys.session, info);
}

export const SIGNED_OUT: SessionInfo = {
  authenticated: false,
  setupRequired: false,
  email: null,
  displayName: null,
};

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LoginBody) => endpoints.login(body),
    onSuccess: (info) => acceptSession(queryClient, info),
  });
}

export function useSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SetupBody) => endpoints.setup(body),
    onSuccess: (info) => acceptSession(queryClient, info),
  });
}

// Set when the reader chose to log out, so the shell doesn't offer "return to where you were".
let explicitSignOut = false;
export function consumeExplicitSignOut(): boolean {
  const value = explicitSignOut;
  explicitSignOut = false;
  return value;
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => endpoints.logout(),
    onMutate: () => {
      explicitSignOut = true;
      // Unsaved compose text kept for crash recovery does not outlive the session.
      clearAllRecovery(browserStorage());
    },
    onSettled: () => {
      setCsrfToken(null);
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
      queryClient.setQueryData(queryKeys.session, SIGNED_OUT);
    },
  });
}

// ---------------------------------------------------------------------------------------------
// Accounts

export const accountsQuery = queryOptions({
  queryKey: queryKeys.accounts,
  queryFn: ({ signal }) => endpoints.accounts(signal),
  // SSE keeps this fresh; the interval is a safety net if the stream is down.
  refetchInterval: 60_000,
});

// Gaps the reader acknowledged but whose undo window is still open: hidden locally, not yet
// acknowledged on the server (so refetches must not bring them back).
const hiddenGapIds = new Set<string>();
let hiddenGapsVersion = 0;
const hiddenGapListeners = new Set<() => void>();

export function withoutHiddenGaps(
  list: AccountSummary[],
  hidden: ReadonlySet<string> = hiddenGapIds,
): AccountSummary[] {
  if (!hidden.size) return list;
  return list.map((a) =>
    a.openGaps.some((g) => hidden.has(g.id))
      ? { ...a, openGaps: a.openGaps.filter((g) => !hidden.has(g.id)) }
      : a,
  );
}

export function setGapHidden(gapId: string, hidden: boolean): void {
  if (hidden) hiddenGapIds.add(gapId);
  else hiddenGapIds.delete(gapId);
  hiddenGapsVersion++;
  for (const listener of hiddenGapListeners) listener();
}

function subscribeHiddenGaps(listener: () => void) {
  hiddenGapListeners.add(listener);
  return () => hiddenGapListeners.delete(listener);
}

export function useAccounts() {
  const version = useSyncExternalStore(
    subscribeHiddenGaps,
    () => hiddenGapsVersion,
    () => hiddenGapsVersion,
  );
  // A new `select` identity whenever the hidden set changes makes TanStack re-run it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const select = useCallback((list: AccountSummary[]) => withoutHiddenGaps(list), [version]);
  return useQuery({ ...accountsQuery, select });
}

function replaceAccount(queryClient: QueryClient, next: AccountSummary) {
  queryClient.setQueryData<AccountSummary[]>(queryKeys.accounts, (list) =>
    list?.map((a) => (a.id === next.id ? next : a)),
  );
}

/** Optimistically flip an account between enabled and disabled. */
export function useSetAccountEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      enabled ? endpoints.enable(id) : endpoints.disable(id),
    onMutate: async ({ id, enabled }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.accounts });
      const previous = queryClient.getQueryData<AccountSummary[]>(queryKeys.accounts);
      queryClient.setQueryData<AccountSummary[]>(queryKeys.accounts, (list) =>
        list?.map((a) =>
          a.id === id
            ? {
                ...a,
                status: enabled ? "ok" : "disabled",
                syncStatus: enabled ? a.syncStatus : "paused",
              }
            : a,
        ),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKeys.accounts, context.previous);
    },
    onSuccess: (account) => replaceAccount(queryClient, account),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.accounts }),
  });
}

export function useRemoveAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpoints.remove(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<AccountSummary[]>(queryKeys.accounts, (list) =>
        list?.filter((a) => a.id !== id),
      );
      void queryClient.invalidateQueries({ queryKey: ["threads"] });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.accounts }),
  });
}

export function useHeartbeat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => endpoints.heartbeat(id),
    onSuccess: (account) => replaceAccount(queryClient, account),
  });
}

export function useSyncNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => endpoints.syncNow(accountId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.accounts }),
  });
}

/** Commit a gap acknowledgement (the card already hid it; see setGapHidden). */
export function useAcknowledgeGap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (gapId: string) => endpoints.acknowledgeGap(gapId),
    onError: (_error, gapId) => setGapHidden(gapId, false),
    onSuccess: async (_data, gapId) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
      setGapHidden(gapId, false);
    },
  });
}

/** Ask the server for a Google consent URL and hand the browser over to it. */
export function useStartGoogleConnect() {
  return useMutation({
    mutationFn: async (input: { accountId?: string; loginHint?: string }) => {
      const { url } = input.accountId
        ? await endpoints.reconnect(input.accountId)
        : await endpoints.connect(input.loginHint);
      return url;
    },
    onSuccess: (url) => {
      if (/^https:\/\//i.test(url) || url.startsWith("/")) window.location.assign(url);
    },
  });
}

// ---------------------------------------------------------------------------------------------
// Threads

/**
 * The split as the server takes it: a label id for the rail, `unread=1` over the inbox for the
 * Unread split (unread threads waiting in the inbox, so Unread is always part of Inbox), the
 * STARRED flag for Starred (stars anywhere), `view=all` for All mail.
 */
export function splitParams(params: ThreadListKey): {
  view?: ThreadListView;
  label?: string;
  unread?: boolean;
} {
  if (params.label) return { label: params.label };
  if (params.view === "unread") return { unread: true };
  if (params.view === "starred") return { label: "STARRED" };
  if (params.view === "all") return { view: "all" };
  return {};
}

export function threadsQuery(params: ThreadListKey) {
  return infiniteQueryOptions({
    queryKey: queryKeys.threads(params),
    queryFn: ({ pageParam, signal }) =>
      endpoints.threads(
        { ...splitParams(params), accountId: params.accountId, cursor: pageParam },
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage, allPages, lastPageParam) =>
      nextCursor(lastPage, allPages, lastPageParam),
    staleTime: 15_000,
  });
}

export function useThreadList(params: ThreadListKey, enabled = true) {
  return useInfiniteQuery({ ...threadsQuery(params), enabled });
}

export function searchQuery(q: string) {
  return queryOptions({
    queryKey: queryKeys.search(q),
    queryFn: ({ signal }) => endpoints.search(q, signal),
    staleTime: 30_000,
  });
}

export function useSearchResults(q: string) {
  return useQuery({ ...searchQuery(q), enabled: q.trim().length > 0 });
}

/** Look through cached lists for a thread summary so the thread view can paint immediately. */
export function findCachedSummary(queryClient: QueryClient, key: string): ThreadSummary | null {
  for (const [, data] of queryClient.getQueriesData<InfiniteData<ThreadListResponse>>({
    queryKey: ["threads"],
  })) {
    for (const page of data?.pages ?? []) {
      const hit = page.threads.find((t) => t.key === key);
      if (hit) return hit;
    }
  }
  for (const root of ["search", "gmailSearch"]) {
    for (const [, data] of queryClient.getQueriesData<ThreadListResponse>({ queryKey: [root] })) {
      const hit = data?.threads.find((t) => t.key === key);
      if (hit) return hit;
    }
  }
  return null;
}

export function threadQuery(key: string, images: boolean) {
  return queryOptions({
    queryKey: queryKeys.thread(key, images),
    queryFn: ({ signal }) => endpoints.thread(key, images, signal),
    staleTime: 60_000,
  });
}

export function useThread(key: string, images: boolean) {
  const queryClient = useQueryClient();
  return useQuery({
    ...threadQuery(key, images),
    placeholderData: (previous): ThreadView | undefined => {
      if (previous && previous.thread.key === key) return previous;
      const summary = findCachedSummary(queryClient, key);
      return summary ? { thread: summary, messages: [] } : undefined;
    },
  });
}

export function prefetchThread(queryClient: QueryClient, key: string): void {
  void queryClient.prefetchQuery(threadQuery(key, false));
}

// ---------------------------------------------------------------------------------------------
// Settings

export function useChangePassword() {
  return useMutation({
    mutationFn: ({ current, next }: { current: string; next: string }) =>
      endpoints.changePassword(current, next),
  });
}

export function useTotpEnroll() {
  return useMutation({ mutationFn: () => endpoints.totpEnroll() });
}

export function useTotpConfirm() {
  return useMutation({ mutationFn: (code: string) => endpoints.totpConfirm(code) });
}

export function useTotpDisable() {
  return useMutation({ mutationFn: (password: string) => endpoints.totpDisable(password) });
}
